import { useState } from 'react';
import { z } from 'zod';
import { useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import SpinnerButton from '@/components/common/SpinnerButton';
import Error from '@/components/common/Error';
import {
  Form,
  FormField,
  FormItem,
  FormControl,
  FormLabel
} from '@/components/ui/form';
import { Checkbox } from '@/components/ui/checkbox';
import { useCoaches } from '@/data/coach';

import {
  useCoachActivity,
  useApproveCoachActivity,
  useCoachActivities
} from '@/data/coachActivity';

const formSchema = z.object({
  hasProfilePicture: z.boolean().refine((val) => val === true, {
    message: 'Must verify profile picture'
  }),
  hasIdentification: z.boolean().refine((val) => val === true, {
    message: 'Must verify identification document'
  }),
  hasFirstAid: z.boolean().refine((val) => val === true, {
    message: 'Must verify first aid document'
  }),
  hasCertifications: z.boolean().refine((val) => val === true, {
    message: 'Must verify certifications'
  }),
  hasSignature: z.boolean().refine((val) => val === true, {
    message: 'Must verify contract signature'
  })
});

const ApproveDialog = ({ setIsOpen }) => {
  const [apiError, setApiError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const coaches = useCoaches();
  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      hasProfilePicture: false,
      hasIdentification: false,
      hasFirstAid: false,
      hasCertifications: false,
      hasSignature: false
    }
  });

  const params = useParams();
  const coachActivity = useCoachActivity(params.coachActivityId);
  const approveCoachActivity = useApproveCoachActivity();
  const coachActivities = useCoachActivities(params.id);

  const onSubmit = async () => {
    try {
      setSubmitting(true);
      setApiError('');
      const updatedCoachActivity = await approveCoachActivity(
        params.coachActivityId
      );
      await coachActivity.mutate(updatedCoachActivity, false);
      await coaches.mutate();
      await coachActivities.mutate();
      setSubmitting(false);
      setIsOpen(false);
    } catch (err) {
      setApiError(err.message);
      setSubmitting(false);
    }
  };

  return (
    <Dialog open onOpenChange={setIsOpen}>
      <DialogContent className="w-[600px]">
        <DialogHeader>
          <DialogTitle>Approve Coach Activity</DialogTitle>
          <DialogDescription>
            Please verify all required documents and information before
            approving this coach activity.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="hasProfilePicture"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center gap-2">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel>Coach has a decent profile picture</FormLabel>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="hasIdentification"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center gap-2">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel>
                      Clear identification document uploaded
                    </FormLabel>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="hasFirstAid"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center gap-2">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel>First aid document uploaded</FormLabel>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="hasCertifications"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center gap-2">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel>
                      Required certifications and uploads verified
                    </FormLabel>
                  </div>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="hasSignature"
              render={({ field }) => (
                <FormItem>
                  <div className="flex items-center gap-2">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <FormLabel>Contract signature is present</FormLabel>
                  </div>
                </FormItem>
              )}
            />

            {apiError && <Error error={apiError} />}
            <DialogFooter>
              <SpinnerButton loading={submitting}>
                Approve Activity
              </SpinnerButton>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default ApproveDialog;
